import { useState, useEffect, useCallback } from 'react';
import { FiPrinter, FiX } from 'react-icons/fi';
import API from '../../shared/api';
import { buildEtiquetaEscPos } from './escpos';
import { qzAvailable, qzPickPrinter, qzPrintRaw } from './qz';
import { code128Svg } from './code128';

const QZ_PRINTER_KEY = 'agorax_ticket_printer';

const esc = (v) =>
  String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Renderiza el código de barras CODE128 como string SVG (síncrono, sin DOM visible).
function barcodeSvg(value) {
  try {
    return code128Svg(value, { height: 40, moduleWidth: 2 });
  } catch (e) {
    return '';
  }
}

function fmtPrecio(n, simbolo) {
  const v = parseFloat(n);
  if (isNaN(v)) return `${simbolo || '$'}0,00`;
  return `${simbolo || '$'}${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function buildEtiquetaHtml({ nombre, codigo, precio, monedaSimbolo, mostrarPrecio }) {
  const svg = barcodeSvg(codigo);
  const precioHtml = mostrarPrecio
    ? `<div class="precio">${esc(fmtPrecio(precio, monedaSimbolo || '$'))}</div>`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Etiqueta</title>
<style>
@page { size: 80mm auto; margin: 0; }
html, body { margin: 0; padding: 0; }
body { width: 80mm; padding: 3mm; box-sizing: border-box; font-family: 'Courier New', monospace; text-align: center; }
.nombre { font-size: 13px; font-weight: bold; margin-bottom: 2mm; word-break: break-word; }
.codigo { font-size: 11px; margin-bottom: 2mm; }
.precio { font-size: 22px; font-weight: bold; margin: 2mm 0; }
svg { max-width: 72mm; }
</style></head><body>
<div class="nombre">${esc(nombre)}</div>
${precioHtml}
${svg}
<div class="codigo">${esc(codigo)}</div>
</body></html>`;
}

export default function TicketArticulo({ producto, modo, onClose }) {
  const [monedas, setMonedas] = useState([]);
  const [monedaSel, setMonedaSel] = useState(null); // codigo de moneda (o 'principal')
  const [printStatus, setPrintStatus] = useState('idle');

  useEffect(() => {
    let mounted = true;
    API.get('/ventas/monedas')
      .then((r) => {
        if (!mounted) return;
        const arr = r.data?.data || [];
        setMonedas(arr);
        const principal = arr.find((m) => m.es_principal) || arr[0];
        if (principal) setMonedaSel(principal.codigo);
      })
      .catch(() => {})
      .finally(() => mounted && setPrintStatus('idle'));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    qzAvailable().then((ok) => mounted && setPrintStatus(ok ? 'ok' : 'noqz')).catch(() => mounted && setPrintStatus('noqz'));
    return () => {
      mounted = false;
    };
  }, []);

  const mostrarPrecio = modo === 'precio';
  const moneda = monedas.find((m) => m.codigo === monedaSel);
  const precioMoneda = mostrarPrecio
    ? parseFloat(producto.precio || 0) * (moneda ? parseFloat(moneda.tasa_cambio) || 1 : 1)
    : null;

  const windowPrint = useCallback(() => {
    const html = buildEtiquetaHtml({
      nombre: producto.descripcion,
      codigo: producto.codigo,
      precio: precioMoneda,
      monedaSimbolo: moneda?.simbolo,
      mostrarPrecio,
    });
    let win = null;
    try {
      win = window.open('', '_blank', 'width=320,height=480');
    } catch {
      win = null;
    }
    if (win && win.document) {
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.addEventListener('load', () => setTimeout(() => { try { win.focus(); win.print(); } catch (e) {} }, 250));
      return;
    }
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:302px;height:480px;border:0;background:#fff;';
    document.body.appendChild(iframe);
    const fdoc = iframe.contentWindow.document;
    fdoc.open();
    fdoc.write(html);
    fdoc.close();
    iframe.addEventListener('load', () => setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 250));
    setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 30000);
  }, [producto, moneda, precioMoneda, mostrarPrecio]);

  const imprimir = useCallback(async () => {
    const bytes = buildEtiquetaEscPos({
      nombre: producto.descripcion,
      codigo: producto.codigo,
      precio: precioMoneda,
      monedaSimbolo: moneda?.simbolo,
    });
    try {
      const preferred = localStorage.getItem(QZ_PRINTER_KEY);
      const printer = await qzPickPrinter(preferred);
      if (printer) {
        await qzPrintRaw(printer, bytes);
        if (!preferred) localStorage.setItem(QZ_PRINTER_KEY, printer);
        setPrintStatus('ok');
        return;
      }
    } catch (e) {
      console.warn('QZ Tray no disponible, usando window.print():', e?.message || e);
    }
    setPrintStatus('fallback');
    windowPrint();
  }, [producto, moneda, precioMoneda, windowPrint]);

  if (!producto) return null;

  const barcode = barcodeSvg(producto.codigo);

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)', padding: 20,
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 18, width: 400, maxWidth: '94%', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>
              {mostrarPrecio ? 'Etiqueta de precio' : 'Etiqueta de identificación'}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{producto.descripcion}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {mostrarPrecio && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Moneda</label>
              <select
                value={monedaSel || ''}
                onChange={(e) => setMonedaSel(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: 13 }}
              >
                {monedas.map((m) => (
                  <option key={m.id} value={m.codigo}>
                    {m.nombre} ({m.simbolo})
                  </option>
                ))}
              </select>
              {mostrarPrecio && (
                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
                  {fmtPrecio(precioMoneda, moneda?.simbolo)}
                </div>
              )}
            </div>
          )}

          <div
            style={{
              background: '#fafafa', border: '1px dashed #d1d5db', borderRadius: 10,
              padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center',
              fontFamily: "'Courier New', monospace",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'center', marginBottom: 8, color: '#000' }}>{producto.descripcion}</div>
            {mostrarPrecio && (
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: '#000' }}>
                {fmtPrecio(precioMoneda, moneda?.simbolo)}
              </div>
            )}
            {barcode && <div style={{ lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: barcode }} />}
            <div style={{ fontSize: 11, marginTop: 6, color: '#000' }}>{producto.codigo}</div>
          </div>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #e8eaef', flexShrink: 0 }}>
          {printStatus === 'noqz' && (
            <div style={{ fontSize: 11, color: '#b45309', background: '#fffbeb', borderRadius: 8, padding: '6px 10px', border: '1px solid #fde68a', marginBottom: 10 }}>
              Ticketera no detectada. Instala y abre QZ Tray, o se usará la impresora del navegador.
            </div>
          )}
          <button
            onClick={imprimir}
            style={{
              width: '100%', padding: 12, borderRadius: 12, border: 'none', background: '#2563eb',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <FiPrinter size={16} /> Imprimir etiqueta
          </button>
        </div>
      </div>
    </div>
  );
}
